import { Box, Chip, Skeleton, SxProps, Theme, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { SIZES } from '../../../../constants/constants';
import { useShallowMergeSxProps } from '../../../../hooks/useShallowMergeSxProps';
import I18n from '../../../../i18n/I18n';
import { APPROVAL_STATUS, APPROVAL_STATUS_STYLE_MAP } from '../../../constants';
import { ApprovalStatus, ApprovalStepStatus } from '../../../types';
import { useReviewApprovalContext } from '../../hooks/useReviewApprovalContext';

const IGNORE_ERROR = true;

export interface ApprovalStatusIndicatorProps {
    status?: ApprovalStatus | ApprovalStepStatus;
    asChip?: boolean;
    sx?: SxProps<Theme>;
}

export default function ApprovalStatusIndicator({ status, asChip, sx }: ApprovalStatusIndicatorProps) {
    const reviewApprovalContextValue = useReviewApprovalContext(IGNORE_ERROR);
    const { approvalData, isFetchingApprovalData } = reviewApprovalContextValue ?? {};

    const resolvedStatus = status || approvalData?.status;
    const isSentOrCancelled = resolvedStatus === APPROVAL_STATUS.sent || resolvedStatus === APPROVAL_STATUS.cancelled;
    const isRejected = resolvedStatus === APPROVAL_STATUS.declined;

    const chipSx = useShallowMergeSxProps({ pl: isSentOrCancelled ? 0.5 : 0.25, pr: 0.25 }, sx);
    const statusIconSx = useMemo(
        () => ({
            mt: isRejected ? 0.25 : undefined
        }),
        [isRejected]
    );

    if (!status && isFetchingApprovalData) {
        if (asChip) {
            return <Skeleton variant="rounded" width={80} height={24} />;
        } else {
            return (
                <Box display="inline-flex" alignItems="center" gap={0.5}>
                    <Skeleton variant="rounded" width={16} height={16} />
                    <Skeleton variant="text" width={60} />
                </Box>
            );
        }
    }

    if (!resolvedStatus || !(resolvedStatus in APPROVAL_STATUS_STYLE_MAP)) {
        return null;
    }

    const { icon: StatusIcon, chipColor, textColor } = APPROVAL_STATUS_STYLE_MAP[resolvedStatus];

    const labelElement = <I18n token={`approval.step.status.${resolvedStatus}`} />;

    return asChip ? (
        <Chip sx={chipSx} color={chipColor} size={SIZES.small} icon={<StatusIcon />} label={labelElement} />
    ) : (
        <Typography
            display="inline-flex"
            alignItems="center"
            gap={0.5}
            textTransform="capitalize"
            pr={1}
            color={textColor}
            sx={sx}
        >
            <StatusIcon fontSize={SIZES.small} sx={statusIconSx} />
            {labelElement}
        </Typography>
    );
}
